/**
 * IssueRow — one ledger issue (gap or regulatory) rendered as a card row.
 *
 * Shared by the per-section inspector Issues tab (``DataGapsTabPanel``) and the
 * submission-wide Issues tab (``SubmissionIssuesTab``) so both stay visually and
 * behaviourally in sync. Presentational only: the parent owns fetching + the
 * jump / dismiss / reopen / resolve side effects and passes them in as
 * callbacks.
 *
 * Look (launch film, "Issues to review"): a quiet card whose top line is the
 * coloured mono kind label (MISSING INFO red, REGULATOR FLAG amber, REVIEWER
 * violet) with the section ref top-right, then a one-line description. Click
 * the card to select its marker in Word and reveal the details + actions.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useState } from "react";
import type { FixOutcome, SectionIssue } from "../../../api/client";
import type { FixIssue } from "../../review/fixProtocol";
import { matchFlagMarker } from "../../services/draftSanitizer";
import { brandColors, fonts, inkColors, kindColors, statusColors, surfaceColors } from "../../tokens";

/** Sort order for status bands — open first, then dismissed / resolved / stale. */
export const STATUS_ORDER: Record<SectionIssue["status"], number> = {
	open: 0,
	dismissed: 1,
	resolved: 2,
	stale: 3,
};

/** Pretty type label from a marker prefix: "[MISSING INFO:" → "Missing info". */
export function prefixLabel(prefix: string): string {
	const raw = prefix
		.replace(/^\[/, "")
		.replace(/[:\]].*$/, "")
		.trim();
	return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

/** Type label + description for an issue's marker text. */
export function issueParts(issue: SectionIssue): { label: string; description: string } {
	const text = issue.marker_text;
	if (issue.kind === "directive_miss") {
		// Marker-less: the text is a synthetic "[INSTRUCTION NOT COVERED: …]".
		const colon = text.indexOf(":");
		return {
			label: "Instruction not covered",
			description: colon !== -1 ? text.slice(colon + 1, -1).trim() : text,
		};
	}
	const prefix = matchFlagMarker(text);
	const label = prefix ? prefixLabel(prefix) : prefixLabel(text);
	const colon = text.indexOf(":");
	const description = colon !== -1 ? text.slice(colon + 1, -1).trim() : "";
	return { label, description };
}

/** An issue as the Fix issues window lists it: its type and what it asks for. */
export function toFixIssue(issue: SectionIssue): FixIssue {
	const { label, description } = issueParts(issue);
	return { id: issue.id, label, description };
}

/** What the row's fix-status chip says, and what clicking it does. */
export interface FixChip {
	label: string;
	/** ``review`` jumps to the change's review card; ``retry`` re-runs the fix. */
	action: "review" | "retry" | null;
	tone: "busy" | "ready" | "quiet" | "warn";
	/** Longer reason (the server's ``last_fix.message``) for the tooltip. */
	detail?: string | null;
}

/** The fix-status chip for one row, or null. Open rows only. ``fixing`` (its
 *  section is in flight) wins; then this pass's live verdict, else the stored
 *  ``last_fix``. "Change ready" needs a pending proposal for the issue — once
 *  the writer approved or rejected it, the verdict no longer applies. */
export function fixChipFor(
	issue: SectionIssue,
	opts: {
		fixing?: boolean;
		liveOutcome?: { outcome: FixOutcome; message?: string | null } | null;
		pendingProposalId?: string | null;
	} = {},
): FixChip | null {
	if (issue.status !== "open") return null;
	if (opts.fixing) return { label: "Fixing…", action: null, tone: "busy" };
	if (opts.pendingProposalId) return { label: "Change ready · Review", action: "review", tone: "ready" };
	const fix = opts.liveOutcome ?? issue.last_fix ?? null;
	if (!fix) return null;
	const detail = fix.message ?? null;
	switch (fix.outcome) {
		case "not_in_sources":
			return { label: "Review agent checked: not in your sources", action: null, tone: "quiet", detail };
		case "rewrite_rejected":
			return { label: "Change didn't fill it: retry", action: "retry", tone: "warn", detail };
		case "busy":
			return { label: "Asthra is already on this section", action: null, tone: "busy", detail };
		case "rejected":
			return { label: "You rejected the change: retry", action: "retry", tone: "quiet", detail };
		case "not_placed":
			return { label: "Not found in saved text: save and retry", action: "retry", tone: "warn", detail };
		case "failed":
			return { label: "Fix failed: retry", action: "retry", tone: "warn", detail };
		case "stopped":
			return { label: "Fix stopped: retry", action: "retry", tone: "quiet", detail };
		default:
			// "proposed" / "has_pending" with no pending change left: it was reviewed.
			return null;
	}
}

/** Colour family of an issue's kind label (launch-film Issues cards). */
export type IssueTone = "gap" | "regulator" | "reviewer";

/** Kind label tone: regulatory findings amber, reviewer-style notes (reviewer
 *  markers, language checks, uncovered instructions) violet, every other gap
 *  marker ([MISSING INFO], [MISSING TABLE], [NOT FOUND], …) red. */
export function issueTone(issue: SectionIssue): IssueTone {
	if (issue.kind === "regulatory") return "regulator";
	if (issue.kind === "language" || issue.kind === "directive_miss") return "reviewer";
	const prefix = matchFlagMarker(issue.marker_text);
	if (prefix === "[REGULATOR FLAG:") return "regulator";
	if (prefix === "[REVIEWER:" || prefix === "[LANGUAGE:") return "reviewer";
	return "gap";
}

/** Writer-facing resolution line, e.g. "Resolved — you edited the document". */
function resolutionLabel(issue: SectionIssue): string | null {
	if (issue.status === "open") return null;
	if (issue.status === "stale") return "Superseded — section was regenerated";
	if (issue.status === "dismissed") {
		// Chip already reads "Done"; only add a line when there's a note.
		const note = issue.resolution?.note;
		return note ? `Done — ${note}` : null;
	}
	const method = issue.resolution?.method;
	if (method === "agent_edit") return "Resolved — fixed by the review agent";
	if (method === "writer_manual") return "Resolved — you edited it";
	return "Resolved";
}

/** Chip text for an issue status — dismissed reads as "Done" for writers. */
function statusChipLabel(status: SectionIssue["status"]): string {
	return status === "dismissed" ? "Done" : status;
}

/** Rank a regulatory finding's severity for sorting (critical first). */
export function severityRank(severity?: string | null): number {
	switch ((severity || "").toLowerCase()) {
		case "critical":
			return 0;
		case "major":
			return 1;
		case "minor":
			return 2;
		default:
			return 3;
	}
}

/** Label + text colour for a severity, or null when it isn't a real severity
 *  (e.g. the legacy "info" that predates per-finding severity). */
function severityMeta(severity?: string | null): { label: string; color: string } | null {
	switch ((severity || "").toLowerCase()) {
		case "critical":
			return { label: "Critical", color: statusColors.danger };
		case "major":
			return { label: "Major", color: kindColors.library };
		case "minor":
			return { label: "Minor", color: inkColors.ink3 };
		default:
			return null;
	}
}

/** Human tag for a regulatory finding's category, or null. */
function categoryLabel(category?: string | null): string | null {
	switch ((category || "").toLowerCase()) {
		case "precedent_signal":
		case "precedent_note":
			return "Precedent";
		case "source_consistency":
			return "Consistency";
		case "guidance_expectation":
			return "Guidance";
		default:
			return null;
	}
}

const useStyles = makeStyles({
	row: {
		display: "flex",
		alignItems: "flex-start",
		gap: "8px",
		width: "100%",
		boxSizing: "border-box",
		borderRadius: "8px",
		border: `1px solid ${surfaceColors.line}`,
		background: "#FFFFFF",
		padding: "9px 11px",
		fontFamily: fonts.body,
		transitionProperty: "border-color, background",
		transitionDuration: "120ms",
		":hover": { border: `1px solid ${surfaceColors.lineStrong}` },
	},
	// The one open (expanded) card — the tabs keep at most one expanded.
	rowExpanded: {
		border: `1px solid ${brandColors.primary}`,
		background: brandColors.light,
		":hover": { border: `1px solid ${brandColors.primary}` },
	},
	// Done rows (resolved / dismissed / stale) recede.
	rowDone: { opacity: 0.6 },
	rowBody: { flex: "1 1 auto", minWidth: 0 },
	// The card's summary (label line + description) — one button so a click
	// selects the marker in Word and toggles the details.
	summaryBtn: {
		display: "block",
		width: "100%",
		border: "none",
		background: "transparent",
		padding: 0,
		margin: 0,
		textAlign: "left",
		cursor: "pointer",
		fontFamily: fonts.body,
		":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "2px", borderRadius: "4px" },
	},
	rowTop: {
		display: "flex",
		alignItems: "baseline",
		justifyContent: "space-between",
		gap: "8px",
		marginBottom: "4px",
	},
	kindLabel: {
		fontFamily: fonts.mono,
		fontSize: "10px",
		fontWeight: 600,
		letterSpacing: "0.6px",
		textTransform: "uppercase",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	toneGap: { color: statusColors.danger },
	// Deeper amber than ``statusColors.warn`` so 10px text stays legible on white.
	toneRegulator: { color: kindColors.library },
	toneReviewer: { color: brandColors.primary },
	ref: {
		fontFamily: fonts.mono,
		fontSize: "10px",
		letterSpacing: "0.6px",
		textTransform: "uppercase",
		color: inkColors.ink4,
		whiteSpace: "nowrap",
		flex: "0 0 auto",
		fontVariantNumeric: "tabular-nums",
	},
	rowDesc: {
		fontSize: "12px",
		color: inkColors.ink2,
		lineHeight: 1.45,
		wordBreak: "break-word",
	},
	// Collapsed: one line, like the film's cards.
	rowDescClamp: {
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		wordBreak: "normal",
	},
	details: { marginTop: "6px" },
	resolutionLine: {
		fontSize: "10.5px",
		color: inkColors.ink4,
		marginTop: "4px",
		fontStyle: "italic",
	},
	findingIssue: {
		fontSize: "11px",
		color: inkColors.ink3,
		lineHeight: 1.45,
		marginTop: "4px",
		wordBreak: "break-word",
	},
	toResolve: {
		fontSize: "11px",
		color: inkColors.ink2,
		lineHeight: 1.45,
		marginTop: "4px",
		wordBreak: "break-word",
	},
	metaLabel: {
		fontFamily: fonts.mono,
		fontSize: "9.5px",
		fontWeight: 600,
		letterSpacing: "0.6px",
		textTransform: "uppercase",
		color: inkColors.ink4,
		marginRight: "6px",
	},
	// Severity + category meta line for regulatory findings.
	rowMeta: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
		flexWrap: "wrap",
	},
	// Verified evidence block: quote + source line, one per citation.
	evidenceBlock: {
		marginTop: "6px",
		paddingLeft: "8px",
		borderLeft: `2px solid ${surfaceColors.lineStrong}`,
		display: "flex",
		flexDirection: "column",
		gap: "4px",
	},
	evidenceQuote: {
		fontSize: "10.5px",
		color: inkColors.ink2,
		lineHeight: 1.4,
		fontStyle: "italic",
		wordBreak: "break-word",
	},
	evidenceSource: {
		fontSize: "9.5px",
		color: inkColors.ink4,
		wordBreak: "break-word",
	},
	// Selection checkbox (bulk-resolve), at the card's left edge. Only rendered
	// for open issues via ``selectable``.
	checkbox: {
		marginTop: "1px",
		flex: "0 0 auto",
		cursor: "pointer",
		accentColor: brandColors.primary,
	},
	// Quiet text actions, revealed with the details.
	actions: {
		display: "flex",
		alignItems: "center",
		gap: "14px",
		marginTop: "8px",
	},
	actionBtn: {
		border: "none",
		background: "transparent",
		padding: 0,
		fontFamily: fonts.body,
		fontSize: "11px",
		fontWeight: 600,
		color: inkColors.ink3,
		cursor: "pointer",
		":hover": { color: inkColors.ink, textDecorationLine: "underline" },
		":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "2px" },
	},
	actionPrimary: {
		color: brandColors.primary,
		":hover": { color: brandColors.primary },
	},
	// Fix-status chip (last "Fix with Asthra" verdict / in flight).
	fixChip: {
		display: "inline-flex",
		alignItems: "center",
		marginTop: "6px",
		border: `1px solid ${surfaceColors.line}`,
		borderRadius: "10px",
		background: surfaceColors.paperDim,
		padding: "1px 8px",
		fontFamily: fonts.body,
		fontSize: "10.5px",
		fontWeight: 600,
		color: inkColors.ink3,
		maxWidth: "100%",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	fixChipButton: {
		cursor: "pointer",
		":hover": { textDecorationLine: "underline" },
		":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "2px" },
	},
	fixChipBusy: { color: brandColors.primary, border: `1px solid ${brandColors.primary}` },
	fixChipReady: { color: "#063D2B", background: "#E3FAF1", border: `1px solid ${statusColors.cool}` },
	fixChipWarn: { color: kindColors.library, border: `1px solid ${statusColors.warn}` },
	liveLine: {
		marginTop: "4px",
		fontFamily: fonts.mono,
		fontSize: "10.5px",
		color: inkColors.ink3,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
});

/** Open issues with a document marker — the rows that get a bulk-resolve
 *  checkbox (markerless directive misses have nothing for a resolve to rewrite).
 *  "Select all" in both Issues tabs uses this so its count matches the boxes. */
export function isSelectableIssue(issue: SectionIssue): boolean {
	return issue.status === "open" && issue.kind !== "directive_miss";
}

export interface IssueRowProps {
	issue: SectionIssue;
	/** Section reference shown top-right in mono, e.g. "§9.1.1". The per-section
	 *  Issues tab omits it (every row is the same section). */
	sectionRef?: string;
	/** Select this marker in the live Word document (open rows only). */
	onJump: (issue: SectionIssue) => void;
	/** Dismiss ("Mark N/A") — no document edit required. */
	onDismiss: (issue: SectionIssue) => void;
	/** Reopen a dismissed issue. */
	onReopen: (issue: SectionIssue) => void;
	/** Optional host-wired "resolve with asthra". Button hidden when absent. */
	onResolveWithAsthra?: (issue: SectionIssue) => void;
	/** Bulk-resolve selection: when true (open issues only), render a checkbox. */
	selectable?: boolean;
	/** Whether this row is currently selected. */
	selected?: boolean;
	/** Toggle selection for this issue. */
	onToggleSelect?: (issue: SectionIssue) => void;
	/** Controlled expansion: the host keeps one card open at a time and passes
	 *  whether this is it. Uncontrolled (local state) when omitted. */
	expanded?: boolean;
	/** Controlled expansion toggle — paired with ``expanded``. */
	onToggleExpanded?: (issue: SectionIssue) => void;
	/** The row's section is in a running fix pass — the chip reads "Fixing…". */
	fixing?: boolean;
	/** While fixing: the agent's newest action on this section, streamed live. */
	liveLine?: string | null;
	/** This pass's verdict for the issue (overrides the stored ``last_fix``). */
	liveOutcome?: { outcome: FixOutcome; message?: string | null } | null;
	/** Id of a pending change that fills this issue — "Change ready · Review". */
	pendingProposalId?: string | null;
	/** Jump to the pending change's review card. */
	onReviewChange?: (proposalId: string) => void;
	/** Run the fix again for just this issue. */
	onRetryFix?: (issue: SectionIssue) => void;
	/** One-click "Fix": propose a fix for just this flag, from the whole library.
	 *  Given, it is the row's primary action and chat becomes "Edit in chat". */
	onFixNow?: (issue: SectionIssue) => void;
	/** The pending change for this flag, shown inline when the row is open. */
	inlineChange?: React.ReactNode;
}

/** One ledger card: kind label + section ref, one-line description; click to
 *  jump to the marker and reveal regulatory guidance, resolution and actions. */
export const IssueRow: React.FC<IssueRowProps> = ({
	issue,
	sectionRef,
	onJump,
	onDismiss,
	onReopen,
	onResolveWithAsthra,
	selectable,
	selected,
	onToggleSelect,
	expanded: expandedProp,
	onToggleExpanded,
	fixing,
	liveLine,
	liveOutcome,
	pendingProposalId,
	onReviewChange,
	onRetryFix,
	onFixNow,
	inlineChange,
}) => {
	const styles = useStyles();
	const [localExpanded, setLocalExpanded] = useState(false);
	const expanded = expandedProp ?? localExpanded;
	const { label, description } = issueParts(issue);
	const tone = issueTone(issue);
	const isRegulatory = issue.kind === "regulatory";
	// No marker in the document: nothing to jump to, and "Resolve with asthra"
	// works by rewriting a marker — the writer covers the instruction instead.
	const isMarkerless = issue.kind === "directive_miss";
	const isOpen = issue.status === "open";
	const sevMeta = isRegulatory ? severityMeta(issue.severity) : null;
	const catTag = isRegulatory ? categoryLabel(issue.category) : null;
	const evidence = isRegulatory ? (issue.finding_evidence ?? []) : [];
	const resLabel = resolutionLabel(issue);
	const showCheckbox = Boolean(selectable && isSelectableIssue(issue));
	const canJump = isOpen && !isMarkerless;
	// Top-right meta: section ref, prefixed by the status for done rows.
	const refText = [isOpen ? null : statusChipLabel(issue.status), sectionRef].filter(Boolean).join(" · ");
	const toneClass =
		tone === "regulator" ? styles.toneRegulator : tone === "reviewer" ? styles.toneReviewer : styles.toneGap;
	const fixChip = fixChipFor(issue, { fixing, liveOutcome, pendingProposalId });
	const fixChipClass = mergeClasses(
		styles.fixChip,
		fixChip?.tone === "busy" && styles.fixChipBusy,
		fixChip?.tone === "ready" && styles.fixChipReady,
		fixChip?.tone === "warn" && styles.fixChipWarn,
	);
	const fixChipClick =
		fixChip?.action === "review" && pendingProposalId && onReviewChange
			? () => onReviewChange(pendingProposalId)
			: fixChip?.action === "retry" && onRetryFix && !isMarkerless
				? () => onRetryFix(issue)
				: null;

	const handleSummaryClick = () => {
		if (canJump && !expanded) onJump(issue);
		if (onToggleExpanded) onToggleExpanded(issue);
		else setLocalExpanded((v) => !v);
	};

	return (
		<div
			className={mergeClasses(styles.row, expanded && styles.rowExpanded, !isOpen && styles.rowDone)}
			data-testid="issue-row"
		>
			{showCheckbox ? (
				<input
					type="checkbox"
					className={styles.checkbox}
					checked={Boolean(selected)}
					onChange={() => onToggleSelect?.(issue)}
					aria-label="Select flag to fix with the review agent"
				/>
			) : null}
			<div className={styles.rowBody}>
				<button
					type="button"
					className={styles.summaryBtn}
					onClick={handleSummaryClick}
					aria-expanded={expanded}
					title={canJump ? "Select this marker in the Word document" : undefined}
				>
					<div className={styles.rowTop}>
						<span className={mergeClasses(styles.kindLabel, toneClass)}>{label}</span>
						{refText ? <span className={styles.ref}>{refText}</span> : null}
					</div>
					<div className={mergeClasses(styles.rowDesc, !expanded && styles.rowDescClamp)}>
						{description || issue.marker_text}
					</div>
				</button>
				{fixChip ? (
					fixChipClick ? (
						<button
							type="button"
							className={mergeClasses(fixChipClass, styles.fixChipButton)}
							onClick={fixChipClick}
							title={fixChip.detail ?? undefined}
							data-testid="issue-fix-chip"
						>
							{fixChip.label}
						</button>
					) : (
						<span className={fixChipClass} title={fixChip.detail ?? undefined} data-testid="issue-fix-chip">
							{fixChip.label}
						</span>
					)
				) : null}
				{fixing && liveLine ? (
					<div className={styles.liveLine} title={liveLine} data-testid="issue-fix-live" aria-live="polite">
						{liveLine}
					</div>
				) : null}
				{!expanded && resLabel ? <div className={styles.resolutionLine}>{resLabel}</div> : null}
				{expanded ? (
					<div className={styles.details}>
						{sevMeta || catTag ? (
							<div className={styles.rowMeta}>
								{sevMeta ? (
									<span className={styles.metaLabel} style={{ color: sevMeta.color }}>
										{sevMeta.label}
									</span>
								) : null}
								{catTag ? <span className={styles.metaLabel}>{catTag}</span> : null}
							</div>
						) : null}
						{isRegulatory && issue.finding_issue ? (
							<div className={styles.findingIssue}>{issue.finding_issue}</div>
						) : null}
						{isRegulatory && issue.finding_resolution ? (
							<div className={styles.toResolve}>
								<span className={styles.metaLabel}>To resolve</span>
								{issue.finding_resolution}
							</div>
						) : null}
						{isRegulatory && evidence.length > 0 ? (
							<div className={styles.evidenceBlock}>
								<span className={styles.metaLabel}>Evidence</span>
								{evidence.map((ev, i) => {
									const src = [ev.authority, ev.source_title, ev.locator]
										.map((x) => (x || "").trim())
										.filter(Boolean)
										.join(" — ");
									return (
										// biome-ignore lint/suspicious/noArrayIndexKey: evidence has no id and never reorders
										<div key={i}>
											{ev.quote ? <div className={styles.evidenceQuote}>“{ev.quote}”</div> : null}
											{src ? <div className={styles.evidenceSource}>{src}</div> : null}
										</div>
									);
								})}
							</div>
						) : null}
						{resLabel ? <div className={styles.resolutionLine}>{resLabel}</div> : null}
						{isOpen && inlineChange ? inlineChange : null}
						{isOpen ? (
							<div className={styles.actions}>
								{onFixNow && !isMarkerless && !fixing && !pendingProposalId ? (
									<button
										type="button"
										className={mergeClasses(styles.actionBtn, styles.actionPrimary)}
										onClick={() => onFixNow(issue)}
										title="Search your sources and propose a fix for this flag"
									>
										Fix
									</button>
								) : null}
								{onResolveWithAsthra && !isMarkerless ? (
									<button
										type="button"
										className={mergeClasses(styles.actionBtn, !onFixNow && styles.actionPrimary)}
										onClick={() => onResolveWithAsthra(issue)}
										title={
											onFixNow
												? "Work on this flag with the agent in chat"
												: "Search your sources and fix this issue in the document"
										}
									>
										{onFixNow ? "Edit in chat" : "Fix with agent"}
									</button>
								) : null}
								{canJump ? (
									<button
										type="button"
										className={styles.actionBtn}
										onClick={() => onJump(issue)}
										title="Select this marker in the Word document"
									>
										Show in document
									</button>
								) : null}
								<button
									type="button"
									className={styles.actionBtn}
									onClick={() => onDismiss(issue)}
									title="Mark this gap as not applicable — no document edit needed"
								>
									Mark N/A
								</button>
							</div>
						) : issue.status === "dismissed" ? (
							<div className={styles.actions}>
								<button type="button" className={styles.actionBtn} onClick={() => onReopen(issue)}>
									Reopen
								</button>
							</div>
						) : null}
					</div>
				) : null}
			</div>
		</div>
	);
};

export default IssueRow;
